import { Auth } from 'aws-amplify';
import { useCallback, useContext } from 'react';

import { PortalConfig, PortalConfigContext } from 'src/context';
import history from 'src/history';
import { additionalWorkspaceAllowed } from 'src/routes/onboarding/setup/utils/additionalWorkspaceAllowed';
import { ADDITIONAL_WORKSPACE_PLAN_PARAM } from 'src/routes/settings/plans/additionalWorkspacePlan';

const readAuthEmail = (user: unknown): string | undefined => {
  if (typeof user !== 'object' || user === null || !('attributes' in user)) {
    return undefined;
  }
  const { attributes } = user;
  if (
    typeof attributes !== 'object' ||
    attributes === null ||
    !('email' in attributes)
  ) {
    return undefined;
  }
  const { email } = attributes;
  return typeof email === 'string' ? email : undefined;
};

export const navigateToCreateWorkspace = async (
  portalConfig: PortalConfig,
  createWorkspacePath: string,
): Promise<void> => {
  const destination = createWorkspacePath;
  try {
    const email = readAuthEmail(await Auth.currentAuthenticatedUser());
    if (email) {
      const allowed = await additionalWorkspaceAllowed(portalConfig, email);
      if (!allowed) {
        const params = new URLSearchParams(window.location.search);
        params.set(ADDITIONAL_WORKSPACE_PLAN_PARAM, '1');
        // Stay on this page. A reload here paints the modal, blanks the
        // screen, then paints the modal again. history.location.pathname is
        // already basename-free; window.location.pathname is not and would
        // double up the workspace prefix.
        history.replace({
          pathname: history.location.pathname,
          search: `?${params.toString()}`,
        });
        return;
      }
    }
  } catch {
    // No Cognito session. Keep today's create path.
  }
  // history's basename is the current /w/<workspace> prefix, so only a real navigation reaches the unprefixed path.
  window.location.assign(destination);
};

export const useNavigateToCreateWorkspace = () => {
  const portalConfig = useContext(PortalConfigContext);
  return useCallback(
    (createWorkspacePath: string) => {
      void navigateToCreateWorkspace(portalConfig, createWorkspacePath);
    },
    [portalConfig],
  );
};
